// React
import { Fragment, useEffect, useRef, useState } from "react"

// API
import { useMutation, useQuery } from "react-query"
import { AtributApi } from "api"
import { Api as OrderApi } from "components/views/payments/api"
import { Api } from "../api"

// Components
import { Card, CardBody, Col, Modal, ModalBody, ModalHeader, Row, Table } from "reactstrap"
import { ActionButton, Calendar, Checkbox, Input, InputNumber, Select, Signature } from "components"

// Form
import * as yup from "yup"
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Functions
import { dateConvert, toast, validation } from "functions"
import { cardStyle } from "views/miscView/functions"

// Third-Party Libraries
import useToggle from "@react-hook/toggle"
import uuid from "react-uuid"
import { QrReader } from "react-qr-reader"
import { useNavigate } from "react-router-dom"

// Utilities
import { useDataWithWaiver } from "../utils"

function Gender() {
  // API
  const fetchGenders = useQuery(['atribut', 'genders'], AtributApi.genders)
  
  // Hooks
  const { control } = useFormContext()

  return (
    <Controller
      name="gender"
      control={control}
      render={method => (
        <Select
          label={<div>Gender <span className="text-danger">*</span></div>}
          placeholder="Select Gender..."
          loading={fetchGenders.isLoading}
          defaultValue={fetchGenders?.data?.find(val => val.value === method.field.value)}
          options={fetchGenders?.data}
          errors={method.formState.errors?.gender?.message}
          onChange={e => method.field.onChange(e.value)}
        />
      )}
    />
  )
}

function Nationality() {
  // API
  const fetchCountries = useQuery(['atribut', 'countries'], AtributApi.countries)

  // Hooks
  const { control } = useFormContext()

  return (
    <Controller
      name="nationality"
      control={control}
      render={method => (
        <Select
          label={<div>Nationality <span className="text-danger">*</span></div>}
          placeholder="Select Nationality..."
          loading={fetchCountries.isLoading}
          defaultValue={fetchCountries?.data?.find(val => val.value === method.field.value)}
          options={fetchCountries?.data}
          onChange={e => method.field.onChange(e.value)}
          errors={method.formState.errors?.nationality?.message}
        />
      )}
    />
  )
}

function Policy() {
  // Hooks
  const [isOpen, setOpen] = useState(false)

  const toggle = () => {
    setOpen(prev => !prev)
  }

  return (
    <Fragment>
      <span>
        <ActionButton type="button" color="link" className="border-0 p-0" onClick={toggle}>policy.</ActionButton>
      </span>

      {isOpen && (
        <Modal isOpen toggle={toggle} centered size="lg">
          <ModalHeader toggle={toggle}>Policy</ModalHeader>

          <ModalBody className="text-center">
            <img src={require('assets/images/policy.jpeg')} alt="Policy" className="img-fluid" />
          </ModalBody>
        </Modal>
      )}
    </Fragment>
  )
}

export function FormSection({ data }) {
  // Hooks
  const [booking, setBooking] = useState(null)

  return (
    <Fragment>
      <Card className="my-3 rounded-3 shadow-lg" style={cardStyle}>
        <CardBody>
          <ModalSection onSetBooking={setBooking} />
        </CardBody>
      </Card>

      {booking ? (
        <CodeForm booking={booking} cardStyle={cardStyle} />
      ) : (
        <NoCodeForm data={data} cardStyle={cardStyle} />
      )}
    </Fragment>
  )
}

function ModalSection({ onSetBooking }) {
  // Hooks
  const [code, setCode] = useState(null)
  const [isOpen, toggle] = useToggle(false, true)

  // API
  const fetchCode = useMutation(value => Api.booking(value),
    {
      onSuccess: res => {
        toast("success", "Success", "Scan Booking Code Success")
        onSetBooking(res)
      },
      onError: err => toast("danger", "Error", err.response.data.message)
    }
  )

  useEffect(() => {
    if (code) {
      alert("Success")
      toggle()
      fetchCode.mutate(code)
    }

    return () => {
      setCode(null)
    }

    // eslint-disable-next-line
  }, [code])

  return (
    <Fragment>
      <ActionButton block className="text-white" color="primary" onClick={toggle} loading={fetchCode.isLoading}>Scan Booking Code</ActionButton>

      {isOpen && (
        <Modal centered isOpen toggle={toggle}>
          <ModalHeader toggle={toggle}>Scan Booking Code</ModalHeader>

          <ModalBody>
            <QrReader
              constraints={{
                facingMode: "environment",
                aspectRatio: 1/1
              }}
              scanDelay={300}
              onResult={(result) => {
                if (result) {
                  setCode(result.text)
                }
              }}
              style={{ width: '100%' }}
            />
          </ModalBody>
        </Modal>
      )}
    </Fragment>
  )
}

function CodeForm({ booking, cardStyle }) {
  // Hooks
  const navigate = useNavigate()
  const signatureRef = useRef(null)

  // Form
  const { control, formState, handleSubmit } = useForm({
    defaultValues: {
      booking_date: dateConvert(new Date()).default.format,
      booking_code: booking.booking_code,
      email: booking.guest_email,
      phone: booking.guest_phone,
      policy: false,
      terms: false,
      waiver: false
    },
    resolver: yupResolver(yup.object().shape({
      email: yup.string().label("Email").email().required(),
      phone: yup.string().label("Phone").required(),
      policy: yup.boolean().label("Policy").required().oneOf(validation().mustAccept.value, validation().mustAccept.label),
      terms: yup.boolean().label("Terms and Conditions").required().oneOf(validation().mustAccept.value, validation().mustAccept.label),
      waiver: yup.boolean().label("Waiver").required().oneOf(validation().mustAccept.value, validation().mustAccept.label),
    }))
  })
  const onSubmit = values => {
    return new Promise((resolve) => {
      // Variables
      if (signatureRef.current?.isEmpty()) {
        toast("error", "failed", "Signature is empty")
        return resolve()
      }

      OrderApi.store({
        ...values,
        additional_cost: false,
        unique_id: uuid(),
        walk_in: true,
        signature: signatureRef.current?.toDataURL('image/png')
      }).then(res => {
        const data = res.data

        navigate(`/success?unique_id=${data.link}`, {
          replace: true,
          state: { barcode: data.barcode }
        })
      }).catch(err => {
        toast("error", "Error", err.response.statusText)
      }).finally(resolve)
    })
  }

  return (
    <FormProvider control={control}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="my-3">
          <Card className="rounded-3 shadow-lg" style={cardStyle}>
            <CardBody>
              <div className="container-md">
                <div className="mb-2">
                  <h5 className="fw-bold">Guest Details</h5>

                  <Row>
                    <Col md={6}>
                      <section className="my-1">
                        <div className="fw-bold">Booking Code</div>
                        <div>{booking.booking_code}</div>
                      </section>
                    </Col>

                    <Col md={6}>
                      <section className="my-1">
                        <div className="fw-bold">Name</div>
                        <div>{booking.guest_name}</div>
                      </section>
                    </Col>

                    <Col md={6}>
                      <Controller
                        name="phone"
                        control={control}
                        render={method => (
                          <Input
                            {...method.field}
                            label={<div>Phone <span className="text-danger">*</span></div>}
                            ref={null}
                            errors={method.formState.errors?.phone?.message}
                          />
                        )}
                      />
                    </Col>

                    <Col md={6}>
                      <Controller
                        name="email"
                        control={control}
                        render={method => (
                          <Input
                            {...method.field}
                            type="email"
                            label={<div>Email <span className="text-danger">*</span></div>}
                            ref={null}
                            errors={method.formState.errors?.email?.message}
                          />
                        )}
                      />
                    </Col>
                  </Row>

                  <section className="my-1">
                    <div className="fw-bold">Email</div>
                    <div>{booking.guest_email}</div>
                  </section>

                  <Controller
                    name="terms"
                    control={control}
                    render={method => (
                      <Checkbox
                        onChange={() => method.field.onChange(!method.field.value)}
                        value={method.field.value}
                        errors={method.formState.errors?.terms?.message}
                      >
                        I accept all Finns Recreation Club & Finns Beach Club Booking & venue 
                        <span className="ps-1">
                          <a href="https://bookings.finnsbeachclub.com/superadmin/termcondition/get/fr-general" target="_blank" rel="noreferrer">
                            <ActionButton type="button" color="link" className="border-0 p-0">terms and conditions.</ActionButton>
                          </a>
                        </span>
                        ***
                      </Checkbox>
                    )}
                  />

                  <Controller
                    name="policy"
                    control={control}
                    render={method => (
                      <Checkbox
                        onChange={() => method.field.onChange(!method.field.value)}
                        value={method.field.value}
                        errors={method.formState.errors?.policy?.message}
                      >
                        I understand and accept the Finns Recreation Club & Finns Beach Club Booking face & head tattoo <Policy /> ***
                      </Checkbox>
                    )}
                  />

                  <Controller
                    name="waiver"
                    control={control}
                    render={method => (
                      <Checkbox
                        onChange={() => method.field.onChange(!method.field.value)}
                        value={method.field.value}
                        errors={method.formState.errors?.waiver?.message}
                      >
                        I agree to the 
                        <span className="ps-1">
                          <a href="https://bookings.finnsbeachclub.com/pdf/tc/TC__sw-general.pdf" target="_blank" rel="noreferrer">
                            <ActionButton type="button" color="link" className="border-0 p-0">Splash Waiver.</ActionButton>
                          </a>
                        </span>
                      </Checkbox>
                    )}
                  />

                  <Signature ref={signatureRef} />

                  <div className="text-center mt-2">
                    <ActionButton
                      type="submit"
                      loading={formState.isSubmitting}
                      color="primary"
                      className="text-white"
                      size="lg"
                    >
                      Book Now
                    </ActionButton>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </section>
      </form>
    </FormProvider>
  )
}

function NoCodeForm({ data, cardStyle }) {
  // Hooks
  const navigate = useNavigate()
  const signatureRef = useRef(null)
  const dataWaiver = useDataWithWaiver(data)

  // Form
  const { control, formState, handleSubmit } = useForm({
    defaultValues: {
      booking_date: dateConvert(new Date()).default.format,
      ticket: dataWaiver.formData,
      policy: false,
      terms: false,
      email: "",
      first_name: "",
      gender: "",
      last_name: "",
      nationality: 101,
      phone: "",
      qty_insurance: 0,
      waiver: false
    },
    resolver: yupResolver(yup.object().shape({
      terms: yup.boolean().label("Terms and Conditions").required().oneOf(validation().mustAccept.value, validation().mustAccept.label),
      policy: yup.boolean().label("Policy").required().oneOf(validation().mustAccept.value, validation().mustAccept.label),
      first_name: yup.string().label("First Name").required(),
      last_name: yup.string().label("Last Name").required(),
      phone: yup.string().label("Phone").required(),
      nationality: yup.string().label("Nationality").required(),
      email: yup.string().label("Email").email(validation().email.label).required(),
      gender: yup.string().label("Gender").required(),
      waiver: yup.boolean().label("Waiver").required().oneOf(validation().mustAccept.value, validation().mustAccept.label),
    }))
  })
  const { fields } = useFieldArray({
    control,
    name: 'ticket'
  })
  const onSubmit = values => {
    return new Promise((resolve) => {
      // Variables
      const { ticket, ...restValues }  = values
      const paxTicket = ticket.reduce((acc, val) => acc + val.adult + val.child, 0)

      if (signatureRef.current?.isEmpty()) {
        toast("error", "failed", "Signature is empty")
        return resolve()
      }

      if (paxTicket === 0) {
        toast("error", "failed", "No tickets found")
        return resolve()
      } else if (values.qty_insurance > paxTicket) {
        toast("error", "failed", "Personal insurance can't be more than total product")
        return resolve()
      }

      OrderApi.store({
        ...restValues,
        ticket,
        additional_cost: false,
        unique_id: uuid(),
        walk_in: true,
        signature: signatureRef.current?.toDataURL('image/png')
      }).then(res => {
        const data = res.data

        navigate(`/success?unique_id=${data.link}`, {
          replace: true,
          state: { barcode: data.barcode }
        })
      }).catch(err => {
        toast("error", "Error", err.response.statusText)
      }).finally(resolve)
    })
  }
  console.log()

  return (
    <FormProvider control={control}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="my-3 rounded-3 shadow-lg" style={cardStyle}>
          <CardBody>
            <div style={{ maxHeight: '320px', overflow: "auto" }}>
              <Table>
                <thead>
                  <tr className="text-center">
                    <th>No</th>
                    <th>Name</th>
                    {dataWaiver.waiverHeader.map((item, key) => <th key={key} className="text-capitalize" style={{ width: 10 }}>{item}</th>)}
                  </tr>
                </thead>

                <tbody>
                  {fields.map((field, key) => (
                    <tr key={field.id}>
                      <td className="text-center">{key + 1}</td>
                      <td>{field.name}</td>
                      {dataWaiver.waiverHeader.map((item, waiverKey) => {
                        if (item in field) {
                          return (
                            <td key={waiverKey}>
                              <div className="px-50">
                                <Controller
                                  name={`ticket[${key}][${item}]`}
                                  control={control}
                                  render={method => (
                                    <InputNumber
                                      {...method.field}
                                      ref={null}
                                      noLabel
                                      noMargin
                                      min="0"
                                    />
                                  )}
                                />
                              </div>
                            </td>
                          )
                        }

                        return <td key={waiverKey} />
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        <section className="my-3">
          <Card className="rounded-3 shadow-lg" style={cardStyle}>
            <CardBody>
              <div className="container-md">
                <div className="mb-2">
                  <h5 className="fw-bold">Guest Details</h5>

                  <Controller
                    control={control}
                    name="booking_date"
                    render={({ field }) => <Calendar {...field} label="Booking Date" />}
                  />

                  <Row>
                    <Col md={6}>
                      <Controller
                        name="first_name"
                        control={control}
                        render={method => (
                          <Input
                            {...method.field}
                            label={<div>First Name <span className="text-danger">*</span></div>}
                            ref={null}
                            errors={method.formState.errors?.first_name?.message}
                          />
                        )}
                      />
                    </Col>

                    <Col md={6}>
                      <Controller
                        name="last_name"
                        control={control}
                        render={method => (
                          <Input
                            {...method.field}
                            label={<div>Last Name <span className="text-danger">*</span></div>}
                            ref={null}
                            errors={method.formState.errors?.last_name?.message}
                          />
                        )}
                      />
                    </Col>

                    <Col md={6}>
                      <Nationality />
                    </Col>

                    <Col md={3}>
                      <Controller
                        name="phone"
                        control={control}
                        render={method => (
                          <Input
                            {...method.field}
                            label={<div>Phone <span className="text-danger">*</span></div>}
                            ref={null}
                            errors={method.formState.errors?.phone?.message}
                          />
                        )}
                      />
                    </Col>

                    <Col md={3}>
                      <Controller
                        name="zip"
                        control={control}
                        render={method => (
                          <Input
                            {...method.field}
                            label={<div>Zip/Post Code <span className="text-danger">*</span></div>}
                            ref={null}
                            errors={method.formState.errors?.zip?.message}
                          />
                        )}
                      />
                    </Col>

                    <Col md={6}>
                      <Gender />
                    </Col>

                    <Col md={6}>
                      <Controller
                        name="email"
                        control={control}
                        render={method => (
                          <Input
                            {...method.field}
                            label={<div>Email <span className="text-danger">*</span></div>}
                            ref={null}
                            errors={method.formState.errors?.email?.message}
                          />
                        )}
                      />
                    </Col>
                  </Row>

                  <Controller
                    name="terms"
                    control={control}
                    render={method => (
                      <Checkbox
                        onChange={() => method.field.onChange(!method.field.value)}
                        value={method.field.value}
                        errors={method.formState.errors?.terms?.message}
                      >
                        I accept all Finns Recreation Club & Finns Beach Club Booking & venue 
                        <span className="ps-1">
                          <a href="https://bookings.finnsbeachclub.com/superadmin/termcondition/get/fr-general" target="_blank" rel="noreferrer">
                            <ActionButton type="button" color="link" className="border-0 p-0">terms and conditions.</ActionButton>
                          </a>
                        </span>
                        ***
                      </Checkbox>
                    )}
                  />

                  <Controller
                    name="policy"
                    control={control}
                    render={method => (
                      <Checkbox
                        onChange={() => method.field.onChange(!method.field.value)}
                        value={method.field.value}
                        errors={method.formState.errors?.policy?.message}
                      >
                        I understand and accept the Finns Recreation Club & Finns Beach Club Booking face & head tattoo <Policy /> ***
                      </Checkbox>
                    )}
                  />

                  <Controller
                    name="waiver"
                    control={control}
                    render={method => (
                      <Checkbox
                        onChange={() => method.field.onChange(!method.field.value)}
                        value={method.field.value}
                        errors={method.formState.errors?.waiver?.message}
                      >
                        I agree to the 
                        <span className="ps-1">
                          <a href="https://bookings.finnsbeachclub.com/pdf/tc/TC__sw-general.pdf" target="_blank" rel="noreferrer">
                            <ActionButton type="button" color="link" className="border-0 p-0">Splash Waiver.</ActionButton>
                          </a>
                        </span>
                      </Checkbox>
                    )}
                  />

                  <Signature ref={signatureRef} />

                  <div className="text-center mt-2">
                    <ActionButton
                      type="submit"
                      loading={formState.isSubmitting}
                      color="primary"
                      className="text-white"
                      size="lg"
                    >
                      Book Now
                    </ActionButton>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </section>
      </form>
    </FormProvider>
  )
}